import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Truck, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";

/**
 * Phase 43 — Admin CRUD for the transporter directory.
 *
 * Route: `/admin/transportistas`.
 * Soft-delete: DELETE flips `is_active=false` server-side; historic
 * shipment/order assignments to that transporter are preserved.
 */

const EMPTY_FORM = {
  name: "",
  contact_name: "",
  phone: "",
  email: "",
  coverage_area: "",
  notes: "",
  is_active: true,
};

export default function AdminTransporters() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/transporters");
      setItems(Array.isArray(data) ? data : []);
    } catch (_e) {
      toast.error("No se pudieron cargar los transportistas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      name: t.name || "",
      contact_name: t.contact_name || "",
      phone: t.phone || "",
      email: t.email || "",
      coverage_area: t.coverage_area || "",
      notes: t.notes || "",
      is_active: t.is_active !== false,
    });
    setModalOpen(true);
  };

  const close = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        contact_name: form.contact_name.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        coverage_area: form.coverage_area.trim() || null,
        notes: form.notes.trim() || null,
        is_active: !!form.is_active,
      };
      if (editingId) {
        await api.patch(`/admin/transporters/${editingId}`, body);
        toast.success("Transportista actualizado");
      } else {
        await api.post("/admin/transporters", body);
        toast.success("Transportista creado");
      }
      close();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (t) => {
    if (
      !window.confirm(
        `¿Desactivar el transportista '${t.name}'? Los envíos y pedidos ya asignados a él conservarán la asignación, pero no aparecerá para nuevas asignaciones. Puedes reactivarlo editándolo.`,
      )
    )
      return;
    try {
      await api.delete(`/admin/transporters/${t.id}`);
      toast.success("Transportista desactivado");
      await load();
    } catch (_e) {
      toast.error("Error al desactivar");
    }
  };

  const toggleActive = async (t, next) => {
    try {
      await api.patch(`/admin/transporters/${t.id}`, { is_active: next });
      setItems((prev) => prev.map((x) => (x.id === t.id ? { ...x, is_active: next } : x)));
    } catch (_e) {
      toast.error("No se pudo actualizar el estado");
    }
  };

  return (
    <div data-testid="admin-transporters" className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5 text-[#00953B]" />
          <h1 className="text-3xl font-black tracking-tight">Transportistas</h1>
          <span className="text-sm text-neutral-500">({items.length})</span>
        </div>
        <Button
          onClick={openCreate}
          data-testid="admin-transporters-new-btn"
          className="gap-2 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white"
        >
          <Plus className="h-4 w-4" /> Nuevo transportista
        </Button>
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-neutral-500">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500">
            <Truck className="h-10 w-10 mx-auto text-neutral-300 mb-2" />
            <p className="mb-3">Aún no hay transportistas dados de alta.</p>
            <Button
              onClick={openCreate}
              className="gap-2 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white"
              data-testid="admin-transporters-empty-cta"
            >
              <Plus className="h-4 w-4" /> Crea tu primer transportista
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Nombre</th>
                  <th className="text-left px-3 py-2">Contacto</th>
                  <th className="text-left px-3 py-2">Teléfono</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Zona / Ruta</th>
                  <th className="text-left px-3 py-2 w-24">Activo</th>
                  <th className="text-right px-3 py-2 w-32">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((t) => (
                  <tr
                    key={t.id}
                    data-testid={`admin-transporter-row-${t.id}`}
                    className={`hover:bg-neutral-50/60 ${t.is_active === false ? "opacity-60" : ""}`}
                  >
                    <td className="px-3 py-2 font-semibold text-neutral-900">
                      {t.name}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">
                      {t.contact_name || "—"}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">
                      {t.phone || "—"}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">
                      {t.email || "—"}
                    </td>
                    <td className="px-3 py-2 text-neutral-600 max-w-xs truncate">
                      {t.coverage_area || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <Switch
                        checked={t.is_active !== false}
                        onCheckedChange={(v) => toggleActive(t, v)}
                        data-testid={`admin-transporter-toggle-${t.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(t)}
                          data-testid={`admin-transporter-edit-${t.id}`}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deactivate(t)}
                          data-testid={`admin-transporter-delete-${t.id}`}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={modalOpen} onOpenChange={(v) => (v ? null : close())}>
        <DialogContent className="max-w-xl" data-testid="admin-transporter-modal">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editar transportista" : "Nuevo transportista"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="tr-name">Nombre *</Label>
              <Input
                id="tr-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ej. TransBarna"
                data-testid="admin-transporter-input-name"
                maxLength={120}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="tr-contact">Persona de contacto</Label>
                <Input
                  id="tr-contact"
                  value={form.contact_name}
                  onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                  placeholder="Ej. Ali M."
                  data-testid="admin-transporter-input-contact"
                  maxLength={120}
                />
              </div>
              <div>
                <Label htmlFor="tr-phone">Teléfono</Label>
                <Input
                  id="tr-phone"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="+34 611 000 111"
                  data-testid="admin-transporter-input-phone"
                  maxLength={40}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="tr-email">Email</Label>
              <Input
                id="tr-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="ali@transbarna.es"
                data-testid="admin-transporter-input-email"
                maxLength={200}
              />
            </div>

            <div>
              <Label htmlFor="tr-coverage">Zona / Ruta</Label>
              <Input
                id="tr-coverage"
                value={form.coverage_area}
                onChange={(e) => setForm((f) => ({ ...f, coverage_area: e.target.value }))}
                placeholder="Ej. Barcelona → Tindouf"
                data-testid="admin-transporter-input-coverage"
                maxLength={200}
              />
            </div>

            <div>
              <Label htmlFor="tr-notes">Notas internas</Label>
              <Textarea
                id="tr-notes"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
                placeholder="Ej. Ruta semanal miércoles"
                data-testid="admin-transporter-input-notes"
                maxLength={1000}
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
                data-testid="admin-transporter-input-active"
              />
              Activo (disponible para nuevas asignaciones)
            </label>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={close}
              data-testid="admin-transporter-cancel-btn"
            >
              <X className="h-4 w-4 mr-1" /> Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="admin-transporter-save-btn"
              className="bg-[#00953B] hover:bg-[#007a30] text-white"
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
